<template>
  
  <div>

    <div class="ui large breadcrumb">
      <div class="bold section">{{ title }}</div>
    </div>

    <bookmark-button :label="title" />

    <div class="ui divider"></div>

    <certs-filter ref="filter" @onApply="loadData" />

    <div v-if="loading && !certs.byPayment.length" class="m-t-20 animated fadeIn">
      <i class="spinner circle loading blue icon"></i> Carregando ...
    </div>

    <div v-if="notFound && !loading" class="grey text animated fadeIn m-t-20">
      <i class="info circle icon"></i> Nenhum registro encontrado
    </div>

    <div v-if="certs.byCustomer.length" class="ui grid animated fadeIn">
      <div class="sixteen wide column">
        <div class="ui segment" :class="{ loading }">
          <h4 class="ui dividing header">Totais por Clínicas</h4>
          <table class="ui celled selectable tablet stackable table">
            <thead v-if="$isDesktop">
              <tr>
                <th>Clínica</th>
                <th v-if="showServiceValue">Valor dos Serviços</th>
                <th>Valor Recebido</th>
                <th>Valor Colaborador</th>
                <th>Saldo Colaborador</th>
                <th>Quant. Laudos</th>
                <th></th>
              </tr>  
            </thead>
            <tbody>
              <tr v-for="(data, index) in certs.byCustomer">
                <td>
                  <strong class="mobile-only">Clínica: </strong> {{ data.customerName }}
                </td>
                <td v-if="showServiceValue">
                  <strong class="mobile-only">Valor dos Serviços: </strong> R$ {{ $formatCurrency(data.value) }}
                </td>
                <td>
                  <strong class="mobile-only">Valor Recebido: </strong> R$ {{ $formatCurrency(data.liquidReceivedValue) }}
                </td>
                <td>
                  <strong class="mobile-only">Valor Colaborador: </strong> R$ {{ $formatCurrency(data.employeeValue) }}
                </td>
                <td>
                  <strong class="mobile-only">Saldo Colaborador: </strong> R$ {{ $formatCurrency(data.payableValue) }}
                </td>
                <td>
                  <strong class="mobile-only">Quant. Laudos: </strong> {{ $formatInteger(data.certsCount) }}
                </td>
                <td class="details-cell">
                  <button class="ui small icon button" @click="showEmployeeCerts(data)">
                    <i class="search plus icon"></i>
                  </button>
                </td>
              </tr>                
            </tbody>
            <tfoot>
              <tr>
                <td></td>                
                <td v-if="showServiceValue" class="bold text">R$ {{ $formatCurrency(totals.byCustomer.value) }}</td>
                <td class="bold text">R$ {{ $formatCurrency(totals.byCustomer.liquidReceivedValue) }}</td>
                <td class="bold text">R$ {{ $formatCurrency(totals.byCustomer.employeeValue) }}</td>
                <td class="bold text">R$ {{ $formatCurrency(totals.byCustomer.payableValue) }}</td>
                <td class="bold text">{{ $formatInteger(totals.byCustomer.certsCount) }}</td>
                <td></td>
              </tr>
            </tfoot>              
          </table>          
        </div>
      </div>
    </div>

    <div v-if="anticipations.length" class="ui grid animated fadeIn">
      <div class="sixteen wide column">
        <div class="ui segment" :class="{ loading }">
          <h4 class="ui dividing header">Recebimentos Antecipados</h4>
          <table class="ui celled selectable tablet stackable table">
            <thead v-if="$isDesktop">
              <tr>
                <th>Tipo</th>
                <th>Número</th>
                <th>Data</th>                
                <th>Clínica</th>
                <th>Valor Recebido</th>
                <th></th>
              </tr>  
            </thead>
            <tbody>
              <tr v-for="(data, index) in anticipations">
                <td>
                  <strong class="mobile-only">Tipo: </strong> 
                  {{ data.isReceivable ? 'Título a Receber' : 'Laudo de outro colaborador' }}
                </td>
                <td>
                  <strong class="mobile-only">Número: </strong> 
                  {{ data.isReceivable ? data.receivableId : data.certId }}
                </td>
                <td>
                  <strong class="mobile-only">Data: </strong> {{ $formatDate(data.date) }}
                </td>
                <td>
                  <strong class="mobile-only">Clínica: </strong> {{ data.customerName }}
                </td>                
                <td>
                  <strong class="mobile-only">Valor Recebido: </strong> R$ {{ $formatCurrency(data.value) }}
                </td>                
                <td class="details-cell">
                  <button v-if="data.canViewDetails" class="ui small icon button" @click="showAnticipation(data)">
                    <i class="search plus icon"></i>
                  </button>
                </td>
              </tr>                
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td class="bold text">R$ {{ $formatCurrency(totals.anticipations.value) }}</td>
                <td></td>
              </tr>
            </tfoot>              
          </table>          
        </div>
      </div>
    </div> 

    <div v-if="certs.byPayment.length" class="ui grid animated fadeIn">
      <div class="sixteen wide column">
        <div class="ui segment" :class="{ loading }">
          <h4 class="ui dividing header">Totais por Tipo de Pagamento</h4>
          <div class="ui form m-t-20 m-b-20">
            <div class="fields">
              <div class="two wide field">
                <label class="form-label">Saldo Colaborador</label>
                R$ {{ $formatCurrency(totals.balance) }}
              </div>
              <div class="two wide field">
                <label class="form-label">Antecipações</label>
                R$ {{ $formatCurrency(totals.anticipations.value) }}
              </div>
              <div class="two wide field">
                <label class="form-label">
                  {{ $isEmployee() ? 'Total a Receber' : 'Total a Pagar' }}
                </label>
                R$ {{ $formatCurrency(totals.payableValue) }}
              </div>
            </div>
          </div>
          <table class="ui celled selectable tablet stackable table">
            <thead v-if="$isDesktop">
              <tr>
                <th>Tipo de Pagamento</th>
                <th v-if="showServiceValue">Valor Total</th>
                <th>Percentual</th>
                <th>{{ $isEmployee() ? 'Valor a Receber' : 'Valor a Pagar' }}</th>
                <th></th>
              </tr>  
            </thead>
            <tbody>
              <tr v-for="(data, index) in certs.byPayment">
                <td>
                  <strong class="mobile-only">Tipo de Pagamento: </strong>
                  <span v-html="data.paymentTypeLabel"></span>
                </td>
                <td v-if="showServiceValue">
                  <strong class="mobile-only">Valor Total: </strong> R$ {{ $formatCurrency(data.value) }}
                </td>
                <td>
                  <strong class="mobile-only">Percentual: </strong> {{ $formatCurrency(data.percent) }}%
                </td>
                <td>
                  <strong class="mobile-only">Valor a Pagar: </strong> R$ {{ $formatCurrency(data.payableValue) }}
                </td>
                <td class="details-cell">
                  <button class="ui small icon button" @click="showEmployeeCerts(data)">
                    <i class="search plus icon"></i>
                  </button>
                </td>
              </tr>                
            </tbody> 
            <tfoot>
              <tr>
                <td></td>                
                <td v-if="showServiceValue" class="bold text">R$ {{ $formatCurrency(totals.byPayment.value) }}</td>
                <td class="bold text">100,00%</td>
                <td class="bold text">R$ {{ $formatCurrency(totals.payableValue) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>          
        </div>
      </div>
    </div>   

    <certs-modal ref="certsModal" />

  </div>

</template>

<script>

  import { AppBrowser, moment } from "@/utils"
  import EmployeeInvoiceService from "@/services/employee-invoice-service"
  import FinancialService from "@/services/financial-service"
  import gridPage from "@/mixins/grid-page"
  import CertsFilter from "./filter.vue"
  import CertsModal from "./certs-modal"

  export default {
    
    name: "EmployeeCertsPage",

    mixins: [gridPage],

    data() {
      return {
        filterData: null,
        title: this.$isAdmin() ? "Laudos de Colaboradores" : "Meus Laudos", 
        service: new EmployeeInvoiceService(),
        notFound: false,
        certs: {
          byPayment: [],
          byCustomer: [],
        },
        anticipations: [],  
        totals: {
          byPayment: {},
          byCustomer: {},
          anticipations: {},
        },        
      }
    },

    components: {
      CertsFilter,
      CertsModal,
    },

    computed: {
      showServiceValue() {
        return !this.$isEmployee()
      },
    },

    methods: {

      async loadData(filter) {

        this.loading = true
        this.filterData = filter

        try {

          filter.userId = filter.user ? filter.user.id : null

          this.certs.byPayment = await this.service.getCertsByPaymentType(filter)
          this.certs.byCustomer = await this.service.getCertsByCustomer(filter)

          this.certs.byPayment.forEach((data) => {
            data.paymentTypeLabel = FinancialService.PAYMENT_TYPE[data.paymentType].label
          })

          this.anticipations = await this.service.getEmployeeAnticipations(filter)

          this.notFound = (!this.certs.byPayment.length && !this.anticipations.length)

          this.loadTotals()
          this.updateByPaymentPercentages()
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      loadTotals() {

       let byPayment = { 
         value: 0,
         liquidReceivedValue: 0,
        }

        let byCustomer = {
          value: 0,
          liquidReceivedValue: 0,
          employeeValue: 0,
          payableValue: 0,
          certsCount: 0,
        }

        let anticipations = { 
          value: 0,
        }

        this.certs.byPayment.forEach((data) => {
          byPayment.value += data.value
          byPayment.liquidReceivedValue += data.liquidReceivedValue
        })

        this.certs.byCustomer.forEach((data) => {
          byCustomer.value += data.value
          byCustomer.liquidReceivedValue += data.liquidReceivedValue
          byCustomer.employeeValue += data.employeeValue
          byCustomer.payableValue += data.payableValue
          byCustomer.certsCount += data.certsCount
        })

        this.anticipations.forEach((data) => {
          anticipations.value += data.value
          data.canViewDetails = this.canViewAnticiaptionDetails(data)
        })

        this.totals.byPayment = byPayment
        this.totals.byCustomer = byCustomer
        this.totals.anticipations = anticipations
        this.totals.balance = byCustomer.payableValue
        this.totals.payableValue = (this.totals.balance - anticipations.value)
      },

      canViewAnticiaptionDetails(anticipation) {

        if (anticipation.isReceivable && !this.$isAdmin() && !this.$isFinancial())
          return false

        return true  
      },

      updateByPaymentPercentages() {

        let list = this.certs.byPayment.slice()
        const totalValue = this.totals.byPayment.value
        const payableValue = this.totals.payableValue

        for (let data of list) {
          data.percent = ((data.value / totalValue) * 100)
          data.payableValue = (payableValue * (data.percent / 100))
        }

        this.certs.byPayment = list.slice()
      },

      showEmployeeCerts(data) {
        
        let filter = { ...this.filterData, ...data }

        this.$refs.certsModal.load(filter)
      },

      showAnticipation(data) {

        let route = data.isCert ? `/procs/certs/${data.certId}/show` : `/financial/receivables/${data.receivableId}`
          
        AppBrowser.openNewTab(route)
      },
    },
  }

</script>